import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import api from "../lib/api";
import { useI18n } from "../context/I18nContext";
import { useCart } from "../context/CartContext";
import { ArrowLeft, Minus, Plus, Truck, ShieldCheck, Box } from "lucide-react";
import { toast } from "sonner";

export default function ProductDetail() {
  const { id } = useParams();
  const { t, lang } = useI18n();
  const { add } = useCart();
  const navigate = useNavigate();
  const [p, setP] = useState(null);
  const [qty, setQty] = useState(1);

  useEffect(() => {
    api.get(`/products/${id}`).then(({ data }) => setP(data));
  }, [id]);

  if (!p) return <div className="max-w-7xl mx-auto px-6 py-20 text-slate-500">{t("common.loading")}</div>;

  const onAdd = () => { add(p, qty); toast.success(t("product.added")); };
  const onBuy = () => { add(p, qty); navigate("/cart"); };

  const name = lang === "ar" ? p.name_ar : p.name_en;
  const desc = lang === "ar" ? p.description_ar : p.description_en;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10" data-testid="product-detail">
      <Link to="/shop" className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-slate-900 mb-6">
        <ArrowLeft className="w-4 h-4 rtl-flip" /> {t("product.back")}
      </Link>

      <div className="grid md:grid-cols-2 gap-10">
        <div className="border border-slate-200 bg-slate-50 aspect-square overflow-hidden">
          <img src={p.image_url} alt={name} className="w-full h-full object-cover" />
        </div>

        <div className="flex flex-col">
          <div className="label-mono text-slate-500">{p.sku}{p.brand ? ` · ${p.brand}` : ""}</div>
          <h1 className="font-display font-black text-3xl sm:text-4xl tracking-tighter mt-2">{name}</h1>
          <div className="font-display font-black text-3xl text-[#8D1B3D] mt-6">
            {p.price.toFixed(2)} <span className="text-sm font-bold text-slate-500">{t("common.currency")}</span>
          </div>
          <div className="label-mono text-emerald-600 mt-2">
            {p.stock > 0 ? `${t("shop.stock")} · ${p.stock}` : t("shop.out")}
          </div>

          <p className="text-slate-700 mt-6 leading-relaxed">{desc}</p>

          <div className="mt-8 flex items-center gap-4">
            <div className="flex items-center border border-slate-300">
              <button onClick={() => setQty(Math.max(1, qty - 1))} className="px-3 py-3 hover:bg-slate-100" data-testid="qty-minus">
                <Minus className="w-4 h-4" />
              </button>
              <input
                type="number"
                value={qty}
                onChange={(e) => setQty(Math.max(1, parseInt(e.target.value || 1)))}
                className="w-14 text-center outline-none py-3 font-bold"
                data-testid="qty-input"
              />
              <button onClick={() => setQty(qty + 1)} className="px-3 py-3 hover:bg-slate-100" data-testid="qty-plus">
                <Plus className="w-4 h-4" />
              </button>
            </div>
            <button onClick={onAdd} className="btn-maroon px-6 py-3 font-bold flex-1" data-testid="detail-add-cart">
              {t("shop.addToCart")}
            </button>
            <button onClick={onBuy} className="bg-slate-900 text-white px-6 py-3 font-bold hover:bg-slate-700" data-testid="detail-buy-now">
              {t("product.buyNow")}
            </button>
          </div>

          <div className="mt-10 grid grid-cols-3 gap-0 border border-slate-200">
            <Mini icon={<Truck className="w-5 h-5" />} label={lang === "ar" ? "توصيل سريع" : "Fast delivery"} />
            <Mini icon={<ShieldCheck className="w-5 h-5" />} label={lang === "ar" ? "ضمان الجودة" : "Quality verified"} mid />
            <Mini icon={<Box className="w-5 h-5" />} label={lang === "ar" ? "مخزون حقيقي" : "Live stock"} />
          </div>
        </div>
      </div>
    </div>
  );
}

function Mini({ icon, label, mid }) {
  return (
    <div className={`p-4 flex items-center gap-3 ${mid ? "border-x border-slate-200" : ""}`}>
      <div className="text-[#8D1B3D]">{icon}</div>
      <div className="text-xs font-semibold">{label}</div>
    </div>
  );
}
